import React, { useCallback, useMemo, useState } from 'react';
import { Check, AlertOctagon } from 'lucide-react';
import T from 'Components/Helpers/translate';
import AssistantResource from 'Resources/Assistant';
import AgentApprovalsResource from 'Resources/AgentApprovals';
import WriteCard from './WriteCard';
import EmailDraftCard from './EmailDraftCard';
import { executionFailureMessage } from './approvalAdapters';
import type { ApprovalGroupItem, WriteProposal, EmailDraft } from './types';

type Props = {
	items: ApprovalGroupItem[];
	onWriteItemUpdate: (index: number, update: Partial<WriteProposal>) => void;
	onEmailItemUpdate: (index: number, update: Partial<EmailDraft>) => void;
};

const statusOf = (item: ApprovalGroupItem): string | undefined =>
	item.kind === 'write' ? item.proposal.status : item.draft.status;

const WriteProposalGroupCard = ({ items, onWriteItemUpdate, onEmailItemUpdate }: Props) => {
	const [bulkBusy, setBulkBusy] = useState(false);

	const counts = useMemo(() => {
		let pending = 0;
		let saved = 0;
		let cancelled = 0;
		let error = 0;
		for (const item of items) {
			const status = statusOf(item);
			// Email "saved" is reported as "sent" in EmailDraft.status — count it
			// in the saved bucket so the header summary looks consistent.
			if (status === 'pending' || status === 'saving' || status === 'sending') pending += 1;
			else if (status === 'saved' || status === 'sent') saved += 1;
			else if (status === 'cancelled') cancelled += 1;
			else if (status === 'error') error += 1;
		}
		return { pending, saved, cancelled, error };
	}, [items]);

	const handleApproveAll = useCallback(async () => {
		if (bulkBusy) return;
		setBulkBusy(true);
		try {
			// Approve all currently only loops the WRITE items. Email items
			// need the user to confirm subject/body and let the signature
			// splice happen, so we keep their Send button local to the card
			// — bulk-sending unread emails is a footgun.
			//
			// Resolve each through the approval path (like the per-card Create
			// button) so the approval row is marked approved and the agent sees
			// the outcome in history — NOT executeWrite, which creates the record
			// but leaves the approval pending so the agent thinks nothing happened.
			// skipContinuation avoids spawning one headless resume run per item.
			for (let i = 0; i < items.length; i++) {
				const item = items[i];
				if (item.kind !== 'write') continue;
				if (item.proposal.status !== 'pending') continue;
				onWriteItemUpdate(i, { status: 'saving' });
				try {
					if (!item.proposal.approvalId) {
						// Legacy proposal with no approval row — fall back to direct execute.
						const result = await AssistantResource.executeWrite(
							item.proposal.entity,
							item.proposal.action,
							item.proposal.data
						);
						onWriteItemUpdate(i, { status: 'saved', result });
						continue;
					}
					const { approval } = await AgentApprovalsResource.approve(
						item.proposal.approvalId,
						undefined,
						undefined,
						{ skipContinuation: true }
					);
					// approve resolves 200 even when the executor itself failed
					// (status='rejected', resultPayload.success=false) — surface that.
					const failure = executionFailureMessage(
						approval.resultPayload as Record<string, unknown> | null | undefined
					);
					if (failure !== null) {
						onWriteItemUpdate(i, {
							status: 'error',
							error: failure || T('default.failed') || 'Write failed',
							result: approval.resultPayload ?? undefined,
							resultEntityType: approval.resultEntityType ?? null,
							resultEntityId: approval.resultEntityId ?? null
						});
					} else {
						onWriteItemUpdate(i, {
							status: 'saved',
							result: approval.resultPayload ?? undefined,
							resultEntityType: approval.resultEntityType ?? null,
							resultEntityId: approval.resultEntityId ?? null
						});
					}
				} catch (err) {
					const message = err instanceof Error ? err.message : 'Write failed';
					onWriteItemUpdate(i, { status: 'error', error: message });
				}
			}
		} finally {
			setBulkBusy(false);
		}
	}, [items, onWriteItemUpdate, bulkBusy]);

	const handleRejectAll = useCallback(async () => {
		if (bulkBusy) return;
		setBulkBusy(true);
		try {
			for (let i = 0; i < items.length; i++) {
				const item = items[i];
				if (item.kind !== 'write') continue;
				if (item.proposal.status !== 'pending') continue;
				// Resolve the approval row as rejected (so the agent sees it was
				// declined, not left pending), then reflect locally. skipContinuation
				// avoids a resume run per item.
				if (item.proposal.approvalId) {
					try {
						await AgentApprovalsResource.reject(item.proposal.approvalId, 'cancelled_in_chat', undefined, {
							skipContinuation: true
						});
					} catch (err) {
						// Best-effort — still cancel locally so the card clears.
						console.warn('[WriteProposalGroupCard] bulk reject failed for one item:', err);
					}
				}
				onWriteItemUpdate(i, { status: 'cancelled' });
			}
		} finally {
			setBulkBusy(false);
		}
	}, [items, onWriteItemUpdate, bulkBusy]);

	const headline = T('assistant.proposalsAwaiting') || 'Proposals to review';
	const summaryParts: string[] = [];
	if (counts.pending) summaryParts.push(`${counts.pending} ${T('assistant.proposalCountPending') || 'pending'}`);
	if (counts.saved) summaryParts.push(`${counts.saved} ${T('assistant.proposalCountApproved') || 'approved'}`);
	if (counts.cancelled)
		summaryParts.push(`${counts.cancelled} ${T('assistant.proposalCountCancelled') || 'cancelled'}`);
	if (counts.error) summaryParts.push(`${counts.error} ${(T('default.failed') || 'failed').toLowerCase()}`);

	// Bulk footer only when there are 2+ pending WRITE items (it doesn't act on emails).
	const pendingWrites = items.filter(
		it => it.kind === 'write' && it.proposal.status === 'pending'
	).length;
	const showBulkFooter = pendingWrites > 1;

	return (
		<section className="pr-wgroup-shell">
			<div className="pr-wgroup-shell-head">
				<span>
					{items.length > 1 ? `${headline} (${items.length})` : headline}
					{summaryParts.length > 0 && (
						<span className="pr-wgroup-summary">{` · ${summaryParts.join(' · ')}`}</span>
					)}
				</span>
			</div>
			<div className="pr-wgroup-shell-list">
				{items.map((item, idx) =>
					item.kind === 'write' ? (
						<WriteCard
							key={idx}
							proposal={item.proposal}
							onUpdate={update => onWriteItemUpdate(idx, update)}
						/>
					) : (
						<EmailDraftCard
							key={idx}
							draft={item.draft}
							onUpdate={update => onEmailItemUpdate(idx, update)}
						/>
					)
				)}
			</div>
			{showBulkFooter && (
				<footer className="ws-trace-approval-footer">
					<div className="ws-trace-approval-actions">
						<button
							type="button"
							className="ws-btn ws-btn-sm ws-btn-secondary"
							onClick={handleRejectAll}
							disabled={bulkBusy}
						>
							<AlertOctagon style={{ width: 13, height: 13 }} />
							<span>{T('assistant.rejectAll') || 'Reject all'}</span>
						</button>
						<button
							type="button"
							className="ws-btn ws-btn-sm ws-btn-primary"
							onClick={handleApproveAll}
							disabled={bulkBusy}
						>
							<Check style={{ width: 13, height: 13 }} />
							<span>{T('assistant.approveAll') || 'Approve all'}</span>
						</button>
					</div>
				</footer>
			)}
		</section>
	);
};

export default WriteProposalGroupCard;
